import { atom, useSetAtom } from "jotai"
import { Box, Button, FormControl, FormHelperText, TextField } from "@mui/material"
import { Controller, FormProvider, useForm } from "react-hook-form"
import { yupResolver } from "@hookform/resolvers/yup"
import { InferType, date, object, string } from "yup"
import { DatePicker } from "@mui/x-date-pickers"
import { getErrorMessage } from "@utils/form"
import { format } from "date-fns"

const formValidationSchema = object({
  email: string().email("Invalid email format").optional(),
  fromDate: date().optional().nullable(),
  toDate: date().optional().nullable(),
})

type FormValues = InferType<typeof formValidationSchema>

export type SnapshotLens = {
  email?: string
  fromDate?: string
  toDate?: string
  page?: number
  limit?: number
}

export const snapshotLensAtom = atom<SnapshotLens>({})
if (process.env.NODE_ENV !== "production") {
  snapshotLensAtom.debugLabel = "snapshotLensAtom"
}

const defaultValues: FormValues = {
  email: "",
  fromDate: null,
  toDate: null,
}

export const SnapshotFilter = () => {
  const setLens = useSetAtom(snapshotLensAtom)

  const form = useForm<FormValues>({
    defaultValues,
    resolver: yupResolver(formValidationSchema),
    mode: "onTouched",
    reValidateMode: "onChange",
  })

  const {
    handleSubmit,
    control,
    formState: { errors },
  } = form

  const onSubmit = (values: FormValues) => {
    const formattedValues: SnapshotLens = {
      email: values.email && values.email.trim() !== "" ? values.email : undefined,
      fromDate: values.fromDate ? format(values.fromDate, "yyyy-MM-dd") : undefined,
      toDate: values.toDate ? format(values.toDate, "yyyy-MM-dd") : undefined,
    }
    setLens(formattedValues)
  }

  const handleReset = () => {
    form.reset(defaultValues)
    setLens({})
  }

  return (
    <FormProvider {...form}>
      <Box
        component="form"
        onSubmit={handleSubmit(onSubmit)}
        display="flex"
        gap={2}
        alignItems="flex-start"
      >
        <Controller
          control={control}
          name="email"
          render={({ field }) => (
            <TextField
              {...field}
              label="Email"
              variant="standard"
              size="small"
              error={Boolean(errors.email)}
              helperText={errors.email?.message}
            />
          )}
        />
        <Controller
          control={control}
          name="fromDate"
          render={({ field }) => (
            <FormControl variant="standard" error={Boolean(errors.fromDate)}>
              <DatePicker
                label="From date"
                format="yyyy-MM-dd"
                slotProps={{ textField: { variant: "standard", size: "small" } }}
                {...field}
              />
              <FormHelperText error>{getErrorMessage(errors, "fromDate")}</FormHelperText>
            </FormControl>
          )}
        />
        <Controller
          control={control}
          name="toDate"
          render={({ field }) => (
            <FormControl variant="standard" error={Boolean(errors.toDate)}>
              <DatePicker
                label="To date"
                format="yyyy-MM-dd"
                slotProps={{ textField: { variant: "standard", size: "small" } }}
                {...field}
              />
              <FormHelperText error>{getErrorMessage(errors, "toDate")}</FormHelperText>
            </FormControl>
          )}
        />
        <Button variant="contained" type="submit">
          Apply
        </Button>
        <Button variant="outlined" onClick={handleReset}>
          Reset
        </Button>
      </Box>
    </FormProvider>
  )
}
